// src/context/LanguageContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { locales } from '../lib/locales';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
    return context;
};

export const LanguageProvider = ({ children }) => {
    // Priority: localStorage -> Browser Language -> Region Detection -> Default ('en')
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('ayi_language');
        if (saved) return saved;
        return 'en'; // Default initial state
    });

    const [isDetecting, setIsDetecting] = useState(false);

    const t = (key, replacements = {}) => {
        let text = locales[language]?.[key] || locales['en']?.[key] || key;
        
        // 🔄 Variable Interpolation (e.g. {count} -> 5)
        Object.entries(replacements).forEach(([k, v]) => {
            text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        });
        
        return text;
    };

    const switchLanguage = (newLang) => {
        if (locales[newLang]) {
            setLanguage(newLang);
            localStorage.setItem('ayi_language', newLang);
        }
    };

    // Region Detection Logic
    useEffect(() => {
        const detectRegion = async () => {
            const saved = localStorage.getItem('ayi_language');
            if (saved) return; // Don't override user's previous manual choice

            setIsDetecting(true);
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                
                if (data.country_code === 'IN') {
                    console.log("Region detected: India. Defaulting to Hinglish.");
                    switchLanguage('hi');
                } else {
                    switchLanguage('en');
                }
            } catch (err) {
                console.warn("Region detection failed, defaulting to English", err);
                switchLanguage('en');
            } finally {
                setIsDetecting(false);
            }
        };

        detectRegion();
    }, []);

    const value = {
        language,
        t,
        switchLanguage,
        isDetecting
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
